export interface IVec2 {
	x: number;
	y: number;
}

export const Vec2 = {
	add: (a: IVec2, b: IVec2): IVec2 => {
		return { x: a.x + b.x, y: a.y + b.y };
	},

	sub: (a: IVec2, b: IVec2): IVec2 => {
		return { x: a.x - b.x, y: a.y - b.y };
	},

	negate: (v: IVec2): IVec2 => {
		return { x: -v.x, y: -v.y };
	},

	scale: (v: IVec2, s: number) => {
		return { x: v.x * s, y: v.y * s };
	},

	cross: (a: IVec2, b: IVec2): number => {
		return a.x * b.y - b.x * a.y;
	},

	dot: (a: IVec2, b: IVec2): number => {
		return a.x * b.x + a.y * b.y;
	},

	lerp: (a: IVec2, b: IVec2, t: number): IVec2 => {
		return { x: (1 - t) * a.x + t * b.x, y: (1 - t) * a.y + t * b.y };
	},

	length: (v: IVec2): number => {
		return Math.sqrt(v.x * v.x + v.y * v.y);
	},

	sqrLength: (v: IVec2): number => {
		return v.x * v.x + v.y * v.y;
	},

	distance: (a: IVec2, b: IVec2): number => {
		return Vec2.length(Vec2.sub(b, a));
	},

	sqrDistance: (a: IVec2, b: IVec2): number => {
		const delta = Vec2.sub(b, a);
		return delta.x * delta.x + delta.y * delta.y;
	},

	normalize: (v: IVec2): IVec2 => {
		const len = Vec2.length(v);
		return { x: v.x / len, y: v.y / len };
	},

	rotate: (pos: IVec2, degreesClockwise: number): IVec2 => {
		if (degreesClockwise === 0) {
			return { x: pos.x, y: pos.y };
		}

		let radians = (degreesClockwise * Math.PI) / 180;
		return {
			x: pos.x * Math.cos(radians) - pos.y * Math.sin(radians),
			y: pos.x * Math.sin(radians) + pos.y * Math.cos(radians),
		};
	},

	rotateAroundPoint: (pos: IVec2, degreesClockwise: number, pivot: IVec2): IVec2 => {
		const rotated = Vec2.rotate({ x: pos.x - pivot.x, y: pos.y - pivot.y }, degreesClockwise);
		return { x: rotated.x + pivot.x, y: rotated.y + pivot.y };
	},
};
